import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay, tap } from 'rxjs';
import { ApiService } from './api.service';
import { SaveRigDto } from '@models/save-rig-dto.interface';
import { Rig, RigSummary } from '@models/rig.interface';

@Injectable({ providedIn: 'root' })
export class RigsService {
  private readonly apiService = inject(ApiService);
  private listCache$?: Observable<RigSummary[]>;
  private readonly detailCache = new Map<number, Observable<Rig>>();

  getList(forceRefresh = false): Observable<RigSummary[]> {
    if (forceRefresh || !this.listCache$) {
      this.listCache$ = this.apiService
        .get('rigs')
        .pipe(shareReplay(1));
    }

    return this.listCache$;
  }

  getById(id: number, forceRefresh = false): Observable<Rig> {
    if (forceRefresh || !this.detailCache.has(id)) {
      const request$ = this.apiService
        .get(`rigs/${id}`)
        .pipe(shareReplay(1));
      this.detailCache.set(id, request$);
    }

    return this.detailCache.get(id)!;
  }

  create(dto: SaveRigDto): Observable<Rig> {
    return this.apiService
      .post('rigs', dto)
      .pipe(tap(saved => this.invalidateCache(saved.id)));
  }

  update(id: number, dto: SaveRigDto): Observable<Rig> {
    return this.apiService
      .put(`rigs/${id}`, dto)
      .pipe(tap(saved => this.invalidateCache(saved.id)));
  }

  delete(id: number): Observable<void> {
    return this.apiService
      .delete(`rigs/${id}`)
      .pipe(tap(() => this.invalidateCache(id)));
  }

  private invalidateCache(id?: number): void {
    this.listCache$ = undefined;
    if (id !== undefined) {
      this.detailCache.delete(id);
    }
  }
}
